import type { PageMeta } from '@/types/api'
import styles from './Pagination.module.css'

interface PaginationProps {
  page: PageMeta
  onPageChange: (page: number) => void
  onPageSizeChange?: (size: number) => void
}

const PAGE_SIZES = [25, 50, 100] as const
const SMALLEST_PAGE_SIZE = PAGE_SIZES[0]

/**
 * Pager for the admin lists.
 *
 * *** THE RANGE IS SHOWN, NOT JUST THE PAGE NUMBER ***
 * "Showing 26–50 of 312" tells an admin where they are and how much is left.
 * "Page 2 of 13" makes them do arithmetic to answer the same question.
 */
export function Pagination({
  page,
  onPageChange,
  onPageSizeChange,
}: PaginationProps) {
  const { total, page: current, page_size: pageSize, total_pages: totalPages } = page

  // A single page of results needs no pager. Rendering an inert one is clutter
  // that implies there is somewhere else to go.
  if (total === 0) return null

  const first = (current - 1) * pageSize + 1
  const last = Math.min(current * pageSize, total)

  return (
    <div className={styles.pagination}>
      <p className={styles.summary}>
        Showing <strong className="numeric">{first}</strong>–
        <strong className="numeric">{last}</strong> of{' '}
        <strong className="numeric">{total}</strong>
      </p>

      <div className={styles.controls}>
        {onPageSizeChange && total > SMALLEST_PAGE_SIZE && (
          <label className={styles.pageSize}>
            <span className={styles.pageSizeLabel}>Rows</span>
            <select
              value={pageSize}
              onChange={(event) => onPageSizeChange(Number(event.target.value))}
              className={styles.select}
            >
              {PAGE_SIZES.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          </label>
        )}

        {totalPages > 1 && (
          <div className={styles.buttons}>
            <button
              type="button"
              className={styles.button}
              onClick={() => onPageChange(current - 1)}
              disabled={current <= 1}
              aria-label="Previous page"
            >
              ‹ Prev
            </button>

            <span className={styles.pageIndicator}>
              <span className="numeric">{current}</span>
              <span className={styles.of}>of</span>
              <span className="numeric">{totalPages}</span>
            </span>

            <button
              type="button"
              className={styles.button}
              onClick={() => onPageChange(current + 1)}
              disabled={!page.has_next}
              aria-label="Next page"
            >
              Next ›
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
